/**
 * HTTP transport for the activity log.
 *
 * Two paths:
 *   - `flushOutbox(events)` — normal path. Authenticated POST with
 *     a 5 s timeout; rejects on non-2xx so the caller can keep the
 *     batch in the outbox for the next attempt.
 *   - `sendBeaconBatch(events)` — `pagehide`/`beforeunload` path.
 *     `navigator.sendBeacon` if available + the payload fits the
 *     spec's quota; falls back to `fetch(..., { keepalive: true })`
 *     when the queue is large enough that sendBeacon would refuse.
 *
 * The runtime endpoint is `/api/lx/activity/events` — proxied by
 * Vite (dev) to `http://127.0.0.1:18789` and served directly in
 * production by the per-user lexruntime container.
 */

import type { ActivityEvent } from "@lexiun/lx-obs";

import { getToken } from "@/lib/lexserver-client";

const ENDPOINT = "/api/lx/activity/events";
const POST_TIMEOUT_MS = 5_000;
/**
 * Per-spec sendBeacon quota is 64 KB across all browsers. We give
 * ourselves headroom for the JSON envelope.
 */
const SEND_BEACON_BYTE_BUDGET = 60 * 1024;
/** Max events per HTTP batch (matches the runtime ingest cap). */
export const MAX_BATCH_EVENTS = 100;

export interface FlushResult {
  /** The ids the server confirmed it accepted (or treated as duplicates). */
  ackedIds: string[];
  /** How many events the server reported persisting. */
  acceptedCount: number;
  /** How many events were in the request total. */
  total: number;
}

/**
 * Authenticated POST to the runtime activity endpoint. Throws on
 * non-2xx or network failure — the caller keeps the batch in the
 * outbox to retry on the next flush.
 */
export async function flushOutbox(events: ActivityEvent[]): Promise<FlushResult> {
  if (events.length === 0) {
    return { ackedIds: [], acceptedCount: 0, total: 0 };
  }
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), POST_TIMEOUT_MS);
  try {
    const token = await getToken();
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ events }),
      signal: ctrl.signal,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(`activity flush ${res.status}: ${body.slice(0, 200)}`);
    }
    const data = (await res.json().catch(() => ({}))) as {
      accepted?: number;
      total?: number;
    };
    return {
      ackedIds: events.map((e) => e.id),
      acceptedCount: data.accepted ?? events.length,
      total: data.total ?? events.length,
    };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Best-effort flush for `pagehide` / `beforeunload`. Returns true
 * when the batch was handed off to the network layer (sendBeacon
 * accepted or fetch with keepalive was queued).
 *
 * Distinct from `flushOutbox` because:
 *   - sendBeacon doesn't take an Authorization header — for now we
 *     embed the token in the body. The lexruntime ingest endpoint
 *     already validates the body's identity before persistence
 *     (the JWT sub overrides client-supplied `user_id`).
 *   - We don't await — the page is leaving.
 */
export function sendBeaconBatch(
  events: ActivityEvent[],
  token: string | undefined,
): boolean {
  if (events.length === 0) return true;
  // Trim to MAX_BATCH_EVENTS so a runaway outbox can't oversize
  // the beacon body.
  const batch = events.slice(0, MAX_BATCH_EVENTS);
  const body = JSON.stringify({ events: batch, token });
  const bytes = new TextEncoder().encode(body).length;

  if (bytes <= SEND_BEACON_BYTE_BUDGET && typeof navigator !== "undefined") {
    const blob = new Blob([body], { type: "application/json" });
    if (typeof navigator.sendBeacon === "function") {
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return true;
    }
  }

  // Fallback: fetch with keepalive. Most browsers cap keepalive
  // payloads at 64 KB too, but this is best-effort.
  try {
    void fetch(ENDPOINT, {
      method: "POST",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });
    return true;
  } catch {
    return false;
  }
}
